import React, {useState, useEffect} from 'react';
import {useLocation, useSearchParams} from 'react-router-dom';
import Header from '../../components/molecules/Header/component';
import Footer from '../../components/molecules/Footer/component';
import HeroSection from './HeroSection';
import RolesSection from './RolesSection';
import HowItWorksSection from './HowItWorksSection';
import PlansSection from './PlansSection';
import AnimationSection from './AnimationSection';
import AuthDialog from '../../components/organisms/Forms/auth/component';
import {getInvitedFromCookie, setInvitedFromCookie} from '../../utils/cookiePartnerReferral';
import Seo from '../../components/atoms/Seo/component';
import {trackEvent} from '../../analytics/posthog';

const OverviewPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const invitedFrom = searchParams.get('invitedFrom');
    const [dialogType, setDialogType] = useState<'login' | 'registration' | 'reset' | null>(null);

    useEffect(() => {
        if (invitedFrom) {
            setInvitedFromCookie(invitedFrom);
            setDialogType('registration');
            trackEvent('partner_referral_link_opened', {
                signup_source: 'partner_referral_link',
                invited_from: invitedFrom
            });
            trackEvent('partner_referral_signup_started', {
                signup_source: 'partner_referral_link',
                invited_from: invitedFrom
            });
            trackEvent('signup_started', {
                signup_source: 'partner_referral_link',
                invited_from: invitedFrom
            });
        }
    }, [invitedFrom]);

    useEffect(() => {
        const storedInvitedFrom = getInvitedFromCookie();
        if (location.pathname === '/signup' && !invitedFrom && storedInvitedFrom) {
            setDialogType('registration');
        }
    }, [invitedFrom, location.pathname]);

    useEffect(() => {
        trackEvent('landing_page_viewed');
    }, []);

    return (
        <div>
            <Seo
                title="Promotify"
                description="Promotify helps advertisers launch performance-based campaigns, promoters earn from verified clicks, and partners grow referral revenue."
                canonicalPath="/"
                structuredData={[
                    {
                        '@context': 'https://schema.org',
                        '@type': 'Organization',
                        name: 'Promotify',
                        url: 'https://promotifyai.com',
                        email: 'info@promotifyai.com',
                        address: {
                            '@type': 'PostalAddress',
                            streetAddress: '210 Leigh Road',
                            addressLocality: 'Leigh-on-Sea',
                            addressCountry: 'GB',
                            postalCode: 'SS9 1BS'
                        }
                    },
                    {
                        '@context': 'https://schema.org',
                        '@type': 'SoftwareApplication',
                        name: 'Promotify',
                        applicationCategory: 'BusinessApplication',
                        operatingSystem: 'Web',
                        description: 'A SaaS platform for performance-based campaign promotion, verified click tracking, promoter links, partner referrals, and campaign analytics.',
                        offers: {
                            '@type': 'Offer',
                            priceCurrency: 'USD',
                            price: '45'
                        }
                    }
                ]}
            />
            <Header/>
            <HeroSection/>
            <RolesSection/>
            <HowItWorksSection/>
            <PlansSection/>
            <AnimationSection/>
            <Footer/>

            {dialogType && (
                <AuthDialog
                    type={dialogType}
                    onClose={() => setDialogType(null)}
                    onOpenDialog={type => {
                        setDialogType(type as 'login' | 'registration' | 'reset');
                    }}
                />
            )}
        </div>
    );
};

export default OverviewPage;
